/* TAMAN storefront core: cart (Ajax API + bundled section rendering), cart drawer,
   launch-promo bonus sync, discount codes, the e-Građani age-check round trip,
   mobile menu and the first-visit 18+ notice. No framework, no dependencies. */
(() => {
  const T = window.Taman || {};
  const root = (T.routes && T.routes.root) || '/';
  const url = (path) => `${root.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } },
    sget(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } },
    sset(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* storage blocked */ } },
    sdel(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* storage blocked */ } }
  };

  /* ---------- money ---------- */
  const formatMoney = (cents) => {
    const fmt = T.moneyFormat || '{{amount_with_comma_separator}} €';
    const value = (Number(cents) / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const dot = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const comma = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // the store format may hold &nbsp; so "4,80 €" never breaks; text nodes need the character
    return fmt.replace(/&nbsp;/g, ' ').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_with_comma_separator') return `${dot},${dec}`;
      if (key === 'amount_no_decimals_with_comma_separator') return dot;
      if (key === 'amount_no_decimals') return comma;
      return `${comma}.${dec}`;
    });
  };

  /* ---------- toast ---------- */
  const toastEl = document.querySelector('[data-toast]');
  let toastTimer;
  const toast = (msg) => {
    if (!toastEl || !msg) return;
    toastEl.textContent = msg;
    toastEl.hidden = false;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.classList.remove('is-visible');
      setTimeout(() => { toastEl.hidden = true; }, 320);
    }, 3600);
  };

  /* ---------- drawers (cart + menu) ---------- */
  const scrim = document.createElement('div');
  scrim.className = 'scrim';
  scrim.hidden = true;
  document.body.appendChild(scrim);
  let openDrawer = null;
  let lastFocus = null;

  const focusables = (el) => [...el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')]
    .filter((n) => n.offsetParent !== null);

  const open = (drawer) => {
    if (!drawer) return;
    if (openDrawer && openDrawer !== drawer) close(openDrawer, false);
    lastFocus = document.activeElement;
    openDrawer = drawer;
    scrim.hidden = false;
    drawer.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      scrim.classList.add('is-open');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      const first = focusables(drawer)[0];
      if (first) first.focus({ preventScroll: true });
    });
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
  };

  const close = (drawer = openDrawer, restore = true) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    scrim.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => { if (!drawer.classList.contains('is-open')) { drawer.hidden = true; scrim.hidden = true; } }, 420);
    openDrawer = null;
    if (restore && lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  };

  scrim.addEventListener('click', () => close());
  document.addEventListener('keydown', (e) => {
    if (!openDrawer) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') {
      const items = focusables(openDrawer);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const target = document.getElementById(opener.getAttribute('aria-controls'));
      if (target) {
        e.preventDefault();
        open(target);
        /* the drawer markup may be older than the cart (back/forward cache, Shopify's link
           prefetch, another tab): bring it up to date every time the bag is opened */
        if (target.id === 'CartDrawer') syncCart();
      }
      return;
    }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); close(); }
  });

  /* ---------- cart ---------- */
  /* Every cart read-modify-write (adds, steppers, codes, the age token, the sync on load and on
     return to the page) runs in ONE queue, so no sync is ever skipped or based on an old cart.
     The launch gift and the deposit are worked out from the fresh cart and applied in as few
     writes as possible; the last write brings the drawer and count HTML along (bundled section
     rendering), so the page always shows the cart the server ended with. */
  const SECTIONS = ['cart-drawer', 'cart-count'];
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const upper = (s) => String(s || '').toUpperCase();
  const failMsg = () => (T.strings && T.strings.addError) || '';
  const json = async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data.description || data.message || failMsg() || 'Greška';
      return { ok: false, message, data };
    }
    return { ok: true, data };
  };
  /* 409 (too many simultaneous cart requests) and 429 (rate limit) mean nothing was applied: retry once */
  const post = async (path, body, retry = true) => {
    const res = await fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (retry && (res.status === 409 || res.status === 429)) { await wait(900); return post(path, body, false); }
    return json(res);
  };
  const withSections = (body) => ({ ...body, sections: SECTIONS.join(','), sections_url: url('cart') });

  /* true only when the drawer came back (Shopify returns null for a section that failed to render);
     markup is swapped, and taman:cart-rendered fired, only where something actually changed, so a
     background re-sync neither wipes a half-typed code nor makes the cart page fetch itself again */
  const renderSections = (sections, quiet = false) => {
    if (!sections || !sections['cart-drawer']) return false;
    let changed = false;
    SECTIONS.forEach((id) => {
      if (!sections[id]) return;
      const fresh = new DOMParser().parseFromString(sections[id], 'text/html').querySelector(`[data-section-target="${id}"]`);
      if (!fresh) return;
      document.querySelectorAll(`[data-section-target="${id}"]`).forEach((target) => {
        if (target.innerHTML === fresh.innerHTML) return;
        target.innerHTML = fresh.innerHTML;
        changed = true;
      });
    });
    if (changed) document.dispatchEvent(new CustomEvent('taman:cart-rendered', { detail: { quiet } }));
    return true;
  };

  /* drawer + count straight from the server, never from the HTTP cache; one retry.
     No "Accept: application/json" here: with it Shopify answers /cart with the cart JSON and
     ignores ?sections, so nothing would render (verified on the store 2026-10-05). */
  const refresh = async (quiet = false) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await fetch(`${url('cart')}?sections=${SECTIONS.join(',')}`, { cache: 'no-store' });
        if (res.ok && renderSections(await res.json(), quiet)) return true;
      } catch (e) { /* offline or blocked: retry once */ }
      if (!attempt) await wait(700);
    }
    return false;
  };

  const getCart = async () => {
    const res = await fetch(url('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  };

  /* What the cart needs so that the launch gift and the deposit match its packs (null = nothing).
     Gift: one bonus line for the largest qualifying pack plus the launch code.
     Deposit: one povratna-naknada unit per paid can; free launch cans are not in the map (G3). */
  const PROMO_OFF = 'taman:promo-off';
  const planFor = (cart) => {
    const items = cart.items || [];
    const plan = { keyed: {}, byVariant: {}, add: [], discount: undefined };
    const promo = window.TamanPromo;
    if (promo) {
      const promoCode = upper(promo.code);
      const codes = cart.discount_codes || [];
      const entry = codes.find((d) => upper(d.code) === promoCode);
      /* a code that does not apply (typo, B2B cart under 200 €) neither blocks the gift nor stays */
      const others = codes.filter((d) => d.code && upper(d.code) !== promoCode && d.applicable !== false).map((d) => d.code);
      const bonusIds = Object.values(promo.bonus || {}).map(Number);
      const bonusLines = items.filter((i) => bonusIds.includes(Number(i.variant_id)));
      /* Shopify says the launch code does not apply (not started yet, used up): stop adding a
         gift line that would be charged, for the rest of this visit */
      if (entry && entry.applicable === false && bonusLines.length) store.sset(PROMO_OFF, '1');
      let best = 0;
      items.forEach((i) => { best = Math.max(best, Number((promo.tiers || {})[i.variant_id] || 0)); });
      const wanted = promo.active && store.sget(PROMO_OFF) !== '1' && !others.length && best > 0 ? Number((promo.bonus || {})[best]) || null : null;
      let kept = false;
      bonusLines.forEach((l) => {
        if (!kept && Number(l.variant_id) === wanted) { kept = true; if (l.quantity !== 1) plan.keyed[l.key] = 1; }
        else plan.keyed[l.key] = 0;
      });
      if (wanted && !kept) plan.add.push({ id: wanted, quantity: 1, properties: { 'Lansirni poklon': 'gratis' } });
      if (wanted && !entry) plan.discount = promo.code;
      if (!wanted && entry) plan.discount = others.join(',');
    }
    const dep = window.TamanDeposit;
    if (dep && dep.variant) {
      const depId = Number(dep.variant);
      let cans = 0;
      items.forEach((i) => { cans += Number((dep.cans || {})[i.variant_id] || 0) * i.quantity; });
      const lines = items.filter((i) => Number(i.variant_id) === depId);
      const current = lines.reduce((n, l) => n + l.quantity, 0);
      if (lines.length > 1) lines.forEach((l, idx) => { plan.keyed[l.key] = idx ? 0 : cans; });
      else if (current !== cans) plan.byVariant[depId] = cans;
    }
    const idle = !Object.keys(plan.keyed).length && !Object.keys(plan.byVariant).length && !plan.add.length && plan.discount === undefined;
    return idle ? null : plan;
  };

  /* Line keys are valid only until the cart changes, so key-based updates go in the first write;
     the deposit is set by variant id, which stays valid. The last write is always update.js:
     it returns the whole cart (checked once more) and the drawer + count HTML. */
  const applyPlan = async (plan) => {
    const tail = { updates: plan.byVariant };
    if (plan.discount !== undefined) tail.discount = plan.discount;
    if (!plan.add.length) return post('cart/update.js', withSections({ ...tail, updates: { ...plan.keyed, ...plan.byVariant } }));
    if (Object.keys(plan.keyed).length) {
      const first = await post('cart/update.js', { updates: plan.keyed });
      if (!first.ok) return first;
    }
    const added = await post('cart/add.js', { items: plan.add });
    if (!added.ok) return added;
    return post('cart/update.js', withSections(tail));
  };

  /* applies the plan to a cart response and renders the outcome; true = the page shows the server cart */
  const settle = async (data, quiet = false) => {
    let cart = data;
    let sections = data.sections;
    if (!('item_count' in data)) { /* add.js answers with the added lines only */
      const shown = renderSections(sections, quiet); /* the new pack shows at once */
      cart = await getCart();
      if (!planFor(cart)) return shown || refresh(quiet);
      sections = null;
    }
    for (let round = 0; round < 2; round += 1) {
      const plan = planFor(cart);
      if (!plan) break;
      const res = await applyPlan(plan);
      if (!res.ok) return refresh(quiet);
      cart = res.data;
      sections = res.data.sections;
    }
    return renderSections(sections, quiet) || refresh(quiet);
  };

  /* the queue; while it works the drawer body is aria-busy (dimmed, the empty-cart text hidden) */
  let chain = Promise.resolve();
  let pending = 0;
  let lastSync = 0;
  const setBusy = () => {
    document.querySelectorAll('[data-section-target="cart-drawer"]').forEach((el) => el.setAttribute('aria-busy', pending ? 'true' : 'false'));
  };
  const serial = (task) => {
    pending += 1;
    setBusy();
    const run = chain.then(task);
    chain = run.catch(() => {}).then(() => { pending -= 1; setBusy(); });
    return run;
  };
  /* a cart write; network errors reject (the caller shows an error), later failures fall back to a refresh */
  const mutate = (request) => serial(async () => {
    const res = await request();
    if (!res.ok) return res;
    let rendered = false;
    try { rendered = await settle(res.data); } catch (e) { rendered = await refresh(); }
    lastSync = Date.now();
    return { ...res, rendered };
  });
  /* read the server cart, fix gift/deposit if needed, render; calls made while one waits share it */
  let queuedSync = null;
  const syncCart = () => queuedSync || (queuedSync = serial(async () => {
    queuedSync = null;
    let rendered = false;
    try {
      const [cart, shown] = await Promise.all([getCart(), refresh(true)]);
      rendered = planFor(cart) ? await settle(cart, true) : shown;
    } catch (e) { rendered = await refresh(true); }
    lastSync = Date.now();
    return rendered;
  }));

  const TamanCart = {
    add(items) {
      return mutate(() => post('cart/add.js', withSections({ items })));
    },
    change(key, quantity) {
      return mutate(() => post('cart/change.js', withSections({ id: key, quantity })));
    },
    /* a code Shopify reports as not applicable is taken off again, so a typo never blocks the launch gift */
    discount(code) {
      const entered = upper(code);
      return mutate(async () => {
        const res = await post('cart/update.js', withSections({ discount: code || '' }));
        const entry = res.ok && entered ? (res.data.discount_codes || []).find((d) => upper(d.code) === entered) : null;
        if (!entry || entry.applicable !== false || entered === upper((window.TamanPromo || {}).code)) return res;
        const reverted = await post('cart/update.js', withSections({ discount: '' }));
        return reverted.ok ? { ...reverted, invalid: true } : reverted;
      });
    },
    attribute(key, value) {
      return serial(() => post('cart/update.js', { attributes: { [key]: value } }));
    },
    open() { open(document.getElementById('CartDrawer')); },
    close() { close(); },
    refresh: syncCart,
    sync: syncCart,
    formatMoney
  };
  window.TamanCart = TamanCart;

  /* quantity steppers, remove buttons and the code form inside drawer / cart page */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-line-change]');
    if (!btn) return;
    e.preventDefault();
    const line = btn.closest('[data-line-key]');
    if (!line) return;
    line.classList.add('is-busy');
    try {
      const res = await TamanCart.change(line.dataset.lineKey, Number(btn.dataset.lineChange));
      if (!res.ok) toast(res.message);
    } catch (err) { toast(failMsg()); } finally { line.classList.remove('is-busy'); }
  });
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-discount-form]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = (input && input.value || '').trim();
    form.classList.add('is-busy');
    try {
      const res = await TamanCart.discount(code);
      if (!res.ok) toast(res.message);
      else if (res.invalid) {
        toast(T.strings && T.strings.codeInvalid);
        /* keep what was typed so a typo can be fixed, also when the drawer was re-rendered meanwhile */
        const field = (input && document.getElementById(input.id)) || input;
        if (field) field.value = code;
      }
    } catch (err) { toast(failMsg()); } finally { form.classList.remove('is-busy'); }
  });
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-discount-remove]');
    if (!btn) return;
    e.preventDefault();
    try { await TamanCart.discount(''); } catch (err) { toast(failMsg()); }
  });

  /* plain product forms (PDP etc.) opt in with data-product-form */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const fd = new FormData(form);
    const id = Number(fd.get('id'));
    const quantity = Number(fd.get('quantity') || 1);
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.classList.add('is-loading');
    let res;
    try { res = await TamanCart.add([{ id, quantity }]); } catch (err) { res = { ok: false, message: failMsg() }; } finally { if (btn) btn.classList.remove('is-loading'); }
    if (res.ok) {
      /* never slide open a drawer that could not be brought up to date; the bag syncs when tapped */
      if (res.rendered) TamanCart.open(); else toast(T.strings && T.strings.added);
    } else {
      const err = form.querySelector('[data-form-error]');
      if (err) { err.textContent = res.message; err.hidden = false; } else toast(res.message);
    }
  });

  /* ---------- age check (Zakon o trgovini, čl. 11.a) ---------- */
  const AGE_KEY = 'taman:age-verified';
  const ageVerified = () => {
    const raw = store.sget(AGE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    const ttl = ((T.ageCheck && T.ageCheck.tokenTtlMinutes) || 30) * 60 * 1000;
    return ts && Date.now() - ts < ttl;
  };
  const applyAgeState = () => {
    /* the token must sit on THIS cart: after an order Shopify starts a new cart without it */
    const onCart = !!document.querySelector('[data-age-gate][data-age-token="1"]');
    const ok = ageVerified() && onCart;
    document.documentElement.classList.toggle('is-age-verified', ok);
    document.querySelectorAll('[data-age-gate]').forEach((el) => {
      el.dataset.state = ok ? 'verified' : 'pending';
    });
    document.querySelectorAll('[data-checkout-button]').forEach((b) => {
      if (T.salesMode === 'age_check') b.disabled = !ok;
    });
  };
  document.addEventListener('click', (e) => {
    const start = e.target.closest('[data-age-start]');
    if (!start) return;
    e.preventDefault();
    const base = (T.ageCheck && T.ageCheck.serviceUrl) || '';
    if (!base) { toast(start.dataset.unavailable || ''); return; }
    const back = `${window.location.origin}${url('cart')}`;
    window.location.href = `${base.replace(/\/$/, '')}/start?return=${encodeURIComponent(back)}`;
  });
  const consumeAgeToken = async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('avt');
    const failed = params.get('age');
    if (!token && !failed) return;
    params.delete('avt');
    params.delete('age');
    const clean = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', clean);
    if (token) {
      try {
        const res = await TamanCart.attribute('_age_verification', token);
        if (res.ok) {
          store.sset(AGE_KEY, String(Date.now()));
          applyAgeState();
          await syncCart();
          TamanCart.open();
        }
      } catch (e) { /* offline: the buyer can run the check again */ }
    } else if (failed) {
      store.sdel(AGE_KEY);
      applyAgeState();
      const msg = document.querySelector('[data-age-failed-message]');
      toast(msg ? msg.textContent : '');
    }
  };
  document.addEventListener('taman:cart-rendered', applyAgeState);

  /* ---------- 18+ notice on first visit ---------- */
  const ageNotice = document.getElementById('AgeNotice');
  if (ageNotice && typeof ageNotice.showModal === 'function' && store.get('taman:18') !== 'yes') {
    ageNotice.showModal();
    ageNotice.addEventListener('cancel', (e) => e.preventDefault());
    ageNotice.querySelector('[data-age-yes]')?.addEventListener('click', () => {
      store.set('taman:18', 'yes');
      ageNotice.close();
    });
  }

  /* ---------- boot ---------- */
  const boot = () => {
    applyAgeState();
    consumeAgeToken();
    /* the server-rendered drawer and count may be older than the cart (back/forward cache,
       Shopify's link prefetch, another tab) and the cart may need its gift or deposit:
       sync on load and whenever the page comes back */
    syncCart();
    window.addEventListener('pageshow', (e) => { if (e.persisted) syncCart(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && Date.now() - lastSync > 10000) syncCart();
    });
    document.querySelectorAll('[data-open-cart-on-load]').forEach(() => TamanCart.open());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
